import ResourceLinkDTO from 'models/dto/resourceLinkDTO';

export class InternalAuditUserDTO {
  internalAuditId!: number;

  userId!: string;

  role!: number;
}

export class InternalAuditTaskDTO {
  internalAuditId!: number;

  taskId!: number;
}

export class InternalAuditLinkDTO {
  internalAuditId!: number;

  linkId!: number;
}

export default class InternalAuditDTO {
  tenantId!: string;

  internalAuditId!: number;

  internalAuditTemplateId!: number;

  name!: string;

  status!: number;

  origin!: number;

  startDate?: string;

  endDate?: string;

  conclusion?: string;

  // The planned moment of the Teams meeting: user data, read AND written by the update endpoint
  meetingStart?: string;

  meetingEnd?: string;

  // Teams meeting state: server-owned, the update endpoint ignores these
  meetingEventId?: string;

  meetingOrganizerUserId?: string;

  meetingJoinUrl?: string;

  meetingCreated?: string;

  meetingUpdated?: string;

  meetingIsStale?: boolean;

  invitationSent?: string;

  finalInvitationSent?: string;

  authSchemaId?: number;

  reportLinkId?: number;

  reportPdfLinkId?: number;

  reportGeneratedAtStatus?: number;

  reportGeneratedOn?: string;

  commentTrailId!: number;

  auditTrailId!: number;

  modified!: string;

  users!: InternalAuditUserDTO[];

  tasks!: InternalAuditTaskDTO[];

  links!: InternalAuditLinkDTO[];

  // the standards (tenant norm ids) this occurrence audits against. Undefined on a write = not sent
  // = untouched; an empty list is refused (AUDIT0027); a change past Preparing is refused (AUDIT0007)
  normIds?: number[];
}

// Assessment payload for a single scope item (any family). assessedState is derived from the outcome
// by the server and sent back for display only; writeBackState is the state the audit pushes onto the
// live entity when it is published (undefined = do not change). writeBackStateSet tells the server
// this payload carries a deliberate choice - without it, saving an outcome would reset the state.
export class InternalAuditAssessmentDTO {
  outcome!: number;

  conclusion?: string;

  assessedState?: number;

  writeBackState?: number;

  writeBackStateSet!: boolean;
}

export class InternalAuditTaskLinkDTO {
  taskId!: number;

  relationType!: number;
}

export class InternalAuditTransitionActionDTO {
  type!: string;

  isDone!: boolean;

  doneOn?: string;
}

export class InternalAuditTransitionDTO {
  targetStatus!: number;

  allowed!: boolean;

  // ControllerException code strings (e.g. AUDIT0001), translated client-side the same way a
  // thrown ControllerException is
  blockers!: string[];

  // same code-string contract, but these never disable the transition: shown in the modal,
  // the user decides
  warnings!: string[];

  actions!: InternalAuditTransitionActionDTO[];

  // the audit team has worked on the stored report since it was written, so the update action is
  // offered unticked: bringing their document up to date is their call, not a side effect of
  // clicking through the modal
  reportDocumentEdited?: boolean;

  // the stored report still describes the audit and nobody has touched it: the update action is
  // offered unticked here too, for the opposite reason - there is nothing to bring up to date
  reportUpToDate?: boolean;

  // the scope items behind the missing-findings blocker. The only blocker with a payload, because
  // "which items have no finding" cannot be said in a code string
  missingFindings!: InternalAuditScopeItemRefDTO[];

  // the participants behind the team-permissions blocker, for the same reason: the fix is per
  // person, so the code string alone would leave the user guessing who is blocking the audit
  teamPermissionIssues!: InternalAuditTeamPermissionDTO[];
}

// Whether one team member can act in the role they were given, and if not, why. reason is the
// server's TeamPermissionReasons; the client owns the wording, the way it owns an exception code's.
export class InternalAuditTeamPermissionDTO {
  userId!: string;

  name?: string;

  role!: number;

  granted!: boolean;

  reason!: number;

  // the permission set standing in the way, so the message can point at it
  authSchemaName?: string;
}

// A team to check, which does not have to be saved yet: the editor asks about the participant list
// the manager is still assembling.
export class InternalAuditTeamPermissionRequestDTO {
  authSchemaId?: number;

  participants!: InternalAuditUserDTO[];
}

// The result of bringing the report up to date: the document, and the points from the team's comments
// that the report could not take. The second half exists because the update rebuilds the document,
// which destroys the comment threads - a point that is neither in the report nor here is simply gone.
export class InternalAuditReportUpdateDTO {
  link?: ResourceLinkDTO;

  unappliedComments!: string[];
}

// What the report document of an audit looks like right now, asked before regenerating it:
// regenerating replaces the content of that same document, so the client has to be able to say what
// it is about to overwrite. Everything except hasDocument may be missing - the dates come from
// SharePoint and a failed metadata call still has to leave the general warning standing.
export class InternalAuditReportDocumentStatusDTO {
  hasDocument!: boolean;

  generatedOn?: string;

  lastModified?: string;

  lastModifiedBy?: string;

  isModifiedAfterGenerate!: boolean;
}

export class InternalAuditScopeItemRefDTO {
  entityType!: number;

  entityId!: number;

  name!: string;
}

export class InternalAuditMeetingSlotDTO {
  start!: string;

  end!: string;
}

export class InternalAuditParticipantAvailabilityDTO {
  userId!: string;

  name?: string;

  email?: string;

  role!: number;

  required!: boolean;

  status!: number;

  // overlapping appointments of the participant: times only, the server never sends subjects
  conflicts!: InternalAuditMeetingSlotDTO[];
}

export class InternalAuditMeetingAvailabilityDTO {
  start!: string;

  end!: string;

  participants!: InternalAuditParticipantAvailabilityDTO[];

  suggestedSlots!: InternalAuditMeetingSlotDTO[];

  hasConflicts!: boolean;
}

/**
 * Answer of POST internalaudits/{id}/use-as-template: the series that now carries the setup, plus
 * the draft occurrence (if any) that is still on the OLD setup and can be re-seeded.
 */
export class InternalAuditSaveAsTemplateResultDTO {
  internalAuditTemplateId!: number;

  pendingAuditId?: number;

  pendingAuditName?: string;
}
